
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import UsersTab from '@/components/admin/UsersTab';
import FeedbacksTab from '@/components/admin/FeedbacksTab';
import ProjectsTab from '@/components/admin/ProjectsTab';
import PlatformConfigTab from '@/components/admin/PlatformConfigTab';
import SecurityLogsTab from '@/components/admin/SecurityLogsTab';
import DashboardTab from '@/components/admin/DashboardTab';

const Admin = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [syncCount, setSyncCount] = useState(0);

  useEffect(() => {
    // Vérifier si l'utilisateur est administrateur
    if (profile && profile.role !== 'admin') {
      toast({
        title: "Accès refusé",
        description: "Vous n'avez pas les droits d'administrateur.",
        variant: "destructive"
      });
      navigate('/dashboard');
    } else {
      syncUsersAndFetchData();
    }
  }, [profile, navigate, syncCount]);

  // Cette fonction s'assure que tous les utilisateurs dans auth.users ont un profil correspondant
  const syncUsersAndFetchData = async () => {
    setIsLoading(true);
    try {
      console.log('Starting user sync process...');
      
      // 1. D'abord, synchroniser les utilisateurs pour s'assurer que tous ont un profil
      const { error: syncError } = await supabase.rpc('sync_users_and_profiles');
      
      if (syncError) {
        console.error('Erreur lors de la synchronisation des utilisateurs:', syncError);
        toast({
          title: "Erreur de synchronisation",
          description: "Un problème est survenu lors de la synchronisation des utilisateurs. Les données peuvent être incomplètes.",
          variant: "destructive"
        });
      } else {
        console.log('Synchronisation des utilisateurs réussie');
      }
      
      // 2. Récupérer les utilisateurs après une pause pour s'assurer que la DB est à jour
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    } catch (error) {
      console.error('Erreur générale:', error);
      toast({
        title: "Erreur",
        description: "Un problème est survenu lors de la synchronisation des utilisateurs.",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === 'users' || value === 'dashboard') {
      // Synchroniser et rafraîchir les utilisateurs quand on va sur l'onglet utilisateurs ou dashboard
      setSyncCount(prev => prev + 1);
    }
  };

  const forceSync = () => {
    setSyncCount(prev => prev + 1);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Administration</h1>
        
        <Tabs defaultValue="dashboard" value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6 grid grid-cols-3 md:grid-cols-6 gap-2">
            <TabsTrigger value="dashboard">Tableau de bord</TabsTrigger>
            <TabsTrigger value="users">Utilisateurs</TabsTrigger>
            <TabsTrigger value="feedbacks">Feedbacks</TabsTrigger>
            <TabsTrigger value="projects">Projets</TabsTrigger>
            <TabsTrigger value="config">Configuration</TabsTrigger>
            <TabsTrigger value="security">Sécurité</TabsTrigger>
          </TabsList>
          
          <TabsContent value="dashboard">
            <DashboardTab isLoading={isLoading} onForceSync={forceSync} />
          </TabsContent>
          
          <TabsContent value="users">
            <UsersTab onUserCreated={forceSync} isLoading={isLoading} />
          </TabsContent>
          
          <TabsContent value="feedbacks">
            <FeedbacksTab />
          </TabsContent>
          
          <TabsContent value="projects">
            <ProjectsTab />
          </TabsContent>
          
          <TabsContent value="config">
            <PlatformConfigTab />
          </TabsContent>
          
          <TabsContent value="security">
            <SecurityLogsTab />
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default Admin;
